/**
 * AnalysisQueueStore — creates job docs in resume-jobs to trigger sequential
 * Cloud Function processing. Subscribes to job progress for live UI updates.
 *
 * Job doc (resume-jobs/{jobId}):
 * { type: 'extract'|'score', projectId, candidateIds: [...], promptId,
 *   status: 'pending'|'running'|'complete'|'failed'|'continued',
 *   progress: { total, complete, failed, currentName },
 *   errors: [{ candidateId, fileName, error }],
 *   continuationOf, createdAt, updatedAt }
 */
export class AnalysisQueueStore {
    constructor(db) {
        this._db = db;
        this._jobsCollection = db.collection('resume-jobs');
        this._unsub = null;
        this._progressListeners = [];
    }

    /**
     * Subscribe to active jobs for a project.
     * Calls listeners with the latest job status on every change.
     */
    subscribeProgress(projectId) {
        this.disposeProgress();
        this._unsub = this._jobsCollection
            .where('projectId', '==', projectId)
            .orderBy('createdAt', 'desc')
            .limit(5)
            .onSnapshot(snap => {
                const jobs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                this._notifyProgress(jobs);
            }, err => {
                console.error('Job subscription error:', err);
            });
    }

    onProgress(fn) { this._progressListeners.push(fn); }

    disposeProgress() {
        if (this._unsub) { this._unsub(); this._unsub = null; }
    }

    /**
     * Create an extraction job for candidates that need it.
     * Returns the number of candidates queued.
     */
    async createExtractionJob(candidates, projectId) {
        const ids = candidates
            .filter(c => c.extractionStatus !== 'complete')
            .map(c => c.id);
        if (ids.length === 0) return 0;

        await this._jobsCollection.add({
            type: 'extract',
            projectId,
            candidateIds: ids,
            promptId: null,
            status: 'pending',
            progress: { total: ids.length, complete: 0, failed: 0, currentName: '' },
            errors: [],
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return ids.length;
    }

    /**
     * Create scoring jobs — one job per prompt for candidates missing that score.
     * Returns total candidates queued across all prompts.
     */
    async createScoringJobs(candidates, projectId, promptIds) {
        let totalQueued = 0;
        for (const promptId of promptIds) {
            const ids = candidates.filter(c => {
                if (c.extractionStatus !== 'complete') return false;
                const existing = c.scores?.[promptId];
                return !existing || existing.status !== 'complete';
            }).map(c => c.id);

            if (ids.length === 0) continue;

            await this._jobsCollection.add({
                type: 'score',
                projectId,
                candidateIds: ids,
                promptId,
                status: 'pending',
                progress: { total: ids.length, complete: 0, failed: 0, currentName: '' },
                errors: [],
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            });
            totalQueued += ids.length;
        }
        return totalQueued;
    }

    /**
     * Create a markdown conversion job for candidates missing resumeMarkdown.
     */
    async createConvertMdJob(candidateIds, projectId) {
        if (candidateIds.length === 0) return 0;
        await this._jobsCollection.add({
            type: 'convert-md',
            projectId,
            candidateIds,
            promptId: null,
            status: 'pending',
            progress: { total: candidateIds.length, complete: 0, failed: 0, currentName: '' },
            errors: [], log: [],
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return candidateIds.length;
    }

    /**
     * Create a retry job from a completed/failed job's error list.
     */
    async createRetryJob(originalJob) {
        const failedIds = (originalJob.errors || []).map(e => e.candidateId);
        if (failedIds.length === 0) return 0;

        await this._jobsCollection.add({
            type: originalJob.type,
            projectId: originalJob.projectId,
            candidateIds: failedIds,
            promptId: originalJob.promptId || null,
            status: 'pending',
            progress: { total: failedIds.length, complete: 0, failed: 0, currentName: '' },
            errors: [],
            retryOf: originalJob.id,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return failedIds.length;
    }

    _notifyProgress(jobs) {
        for (const fn of this._progressListeners) fn(jobs);
    }
}
